/**
 * SendTemplateModal
 * Modal para selecionar e enviar uma mensagem template em uma conversa.
 * Reusa TemplateSelectWithPreview (Task 6) e useSendTemplate.
 * SEMPRE tema claro fixo (via shared Modal que faz o override automático).
 */

import { useState } from 'react';
import { Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { TemplateSelectWithPreview } from '@/modules/templates/components/TemplateSelectWithPreview';
import type { TemplateSelectValue } from '@/modules/templates/components/TemplateSelectWithPreview';
import { useSendTemplate } from '@/modules/chat/hooks/useSendTemplate';
import { Icon } from '@/shared/icons';

interface SendTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
  channelId: string;
}

export function SendTemplateModal({
  isOpen,
  onClose,
  conversationId,
  channelId,
}: SendTemplateModalProps) {
  const [value, setValue] = useState<TemplateSelectValue>({
    templateId: '',
    variables: {} as Record<string, string>,
  });
  const [templateValid, setTemplateValid] = useState(false);

  const sendTemplate = useSendTemplate();

  const handleSend = () => {
    if (!value.templateId) return;
    sendTemplate.mutate(
      { conversationId, templateId: value.templateId, variables: value.variables },
      {
        onSuccess: () => {
          onClose();
          // Reset form for next time
          setValue({ templateId: '', variables: {} });
        },
      },
    );
  };

  const handleClose = () => {
    onClose();
    // Reset form when manually closed
    setValue({ templateId: '', variables: {} });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="lg" data-testid="send-template-modal">
      <ModalHeader onClose={handleClose}>
        <Icon name="file-text" className="w-5 h-5" style={{ color: '#8b5cf6' }} />
        <h2 className="text-base font-semibold" style={{ color: '#1e293b' }}>
          Enviar mensagem template
        </h2>
      </ModalHeader>

      <ModalContent>
        <TemplateSelectWithPreview
          channelId={channelId}
          value={value}
          onChange={setValue}
          disabled={sendTemplate.isPending}
          onValidityChange={setTemplateValid}
        />
      </ModalContent>

      <ModalFooter>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={sendTemplate.isPending}
          data-testid="send-template-cancel"
        >
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleSend}
          disabled={!templateValid || sendTemplate.isPending}
          loading={sendTemplate.isPending}
          data-testid="send-template-confirm"
        >
          {sendTemplate.isPending ? 'Enviando...' : 'Enviar'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
